/**
 * Validation Utility Functions
 *
 * Purpose: Centralized validation functions for the OrchestrateHQ AI frontend
 *          including form validation, input sanitization, and data integrity checks.
 *
 * Architecture: Enhanced validation system with composable validators, async support,
 *              and comprehensive error reporting for robust form handling.
 *
 * Dependencies: None (pure functions)
 * Author: Development Team
 * Created: 2025-01-27
 * Last Updated: 2025-08-08
 */

/**
 * Validate email address format
 *
 * Purpose: Ensures email addresses are properly formatted for forms
 * Logic: Uses regex pattern to validate email format
 *
 * @param email - Email address to validate
 * @returns True if valid email format, false otherwise
 */
export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return pattern.test(email);
};

/**
 * Validate password strength and requirements
 *
 * Purpose: Ensures passwords meet security requirements
 * Logic: Checks multiple criteria including length, complexity, and character types
 *
 * @param password - Password to validate
 * @param minLength - Minimum password length (default: 8)
 * @returns Validation result with errors list
 */
export const validatePassword = (
  password: string,
  minLength: number = 8
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!password || typeof password !== 'string') {
    errors.push('Password is required');
    return { valid: false, errors };
  }

  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters long`);
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  // Anything that is not a letter or a digit, as the server judges it (ORCH-396)
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Validate required fields in form data
 *
 * Purpose: Ensures all required data is provided in forms
 * Logic: Checks for presence and non-empty values of required fields
 *
 * @param data - Form data object to validate
 * @param requiredFields - List of required field names
 * @returns Validation result with missing fields
 */
export const validateRequiredFields = (
  data: Record<string, any>,
  requiredFields: string[]
): { valid: boolean; missingFields: string[] } => {
  const missingFields: string[] = [];

  for (const field of requiredFields) {
    if (!(field in data) || data[field] === null || data[field] === undefined) {
      missingFields.push(field);
    } else if (typeof data[field] === 'string' && data[field].trim() === '') {
      missingFields.push(field);
    } else if (Array.isArray(data[field]) && data[field].length === 0) {
      missingFields.push(field);
    }
  }

  return { valid: missingFields.length === 0, missingFields };
};

/**
 * Validate URL format
 *
 * Purpose: Ensures URLs are properly formatted
 * Logic: Uses regex pattern to validate URL format
 *
 * @param url - URL to validate
 * @returns True if valid URL format, false otherwise
 */
export const validateUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate phone number format
 *
 * Purpose: Ensures phone numbers are properly formatted
 * Logic: Removes non-digits and validates length
 *
 * @param phone - Phone number to validate
 * @param countryCode - Country code for validation (default: 'US')
 * @returns True if valid phone number, false otherwise
 */
export const validatePhoneNumber = (phone: string, countryCode: string = 'US'): boolean => {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // US phone number validation (10 or 11 digits)
  if (countryCode === 'US') {
    return digitsOnly.length === 10 || digitsOnly.length === 11;
  }

  // Generic international validation (7-15 digits)
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
};

/**
 * Validate percentage value within specified range
 *
 * Purpose: Ensures percentage values are within valid range
 * Logic: Checks if value is numeric and within min/max bounds
 *
 * @param value - Percentage value to validate
 * @param minValue - Minimum allowed value (default: 0)
 * @param maxValue - Maximum allowed value (default: 100)
 * @returns True if valid percentage, false otherwise
 */
export const validatePercentage = (
  value: number,
  minValue: number = 0,
  maxValue: number = 100
): boolean => {
  if (typeof value !== 'number' || isNaN(value)) {
    return false;
  }

  return value >= minValue && value <= maxValue;
};

/**
 * Validate date range (start date before end date)
 *
 * Purpose: Ensures logical date ranges for planning and scheduling
 * Logic: Converts to Date objects and compares dates
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Validation result with error message
 */
export const validateDateRange = (
  startDate: Date | string,
  endDate: Date | string
): { valid: boolean; error?: string } => {
  try {
    const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { valid: false, error: 'Invalid date format' };
    }

    if (start >= end) {
      return { valid: false, error: 'Start date must be before end date' };
    }

    return { valid: true };
  } catch (error) {
    return { valid: false, error: 'Invalid date format' };
  }
};

/**
 * Validate file upload (size and type)
 *
 * Purpose: Ensures uploaded files meet requirements
 * Logic: Checks file size and MIME type against allowed values
 *
 * @param file - File object to validate
 * @param maxSize - Maximum file size in bytes
 * @param allowedTypes - Array of allowed MIME types
 * @returns Validation result with errors
 */
export const validateFileUpload = (
  file: File,
  maxSize: number,
  allowedTypes: string[] = []
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!file || !(file instanceof File)) {
    errors.push('Invalid file');
    return { valid: false, errors };
  }

  if (file.size > maxSize) {
    errors.push(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    errors.push(`File type must be one of: ${allowedTypes.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Sanitize HTML input to prevent XSS
 *
 * Purpose: Removes potentially dangerous HTML content
 * Logic: Strips HTML tags and encodes special characters
 *
 * @param input - Input string to sanitize
 * @returns Sanitized string
 */
export const sanitizeHtml = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');

  // Encode special characters
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

  return sanitized;
};

/**
 * Validator function interface
 *
 * Purpose: Defines the structure for validator functions
 * Properties: Input value and validation result
 */
export interface Validator<T = any> {
  (value: T): ValidationResult;
}

/**
 * Async validator function interface
 *
 * Purpose: Defines the structure for async validator functions
 * Properties: Input value and promise-based validation result
 */
export interface AsyncValidator<T = any> {
  (value: T): Promise<ValidationResult>;
}

/**
 * Validation result interface
 *
 * Purpose: Standardized validation result structure
 * Properties: Validity status and error messages
 */
export interface ValidationResult {
  /** Validation passed */
  valid: boolean;
  /** Error messages if validation failed */
  errors?: string[];
  /** Warning messages (non-blocking) */
  warnings?: string[];
}

/**
 * Field validation schema interface
 *
 * Purpose: Defines validation rules for a single field
 * Properties: Validators, constraints, and options
 */
export interface FieldSchema {
  /** Field is required */
  required?: boolean;
  /** Field data type */
  type?: string;
  /** Minimum value/length */
  min?: number;
  /** Maximum value/length */
  max?: number;
  /** Regex pattern for validation */
  pattern?: RegExp;
  /** Allowed values for enum fields */
  enum?: any[];
  /** Custom validator functions */
  validators?: Validator[];
  /** Async validator functions */
  asyncValidators?: AsyncValidator[];
  /** Custom error messages */
  messages?: Record<string, string>;
}

/**
 * Form validation schema interface
 *
 * Purpose: Defines validation rules for an entire form
 * Properties: Field schemas and global validators
 */
export interface FormSchema {
  /** Field validation schemas */
  properties: Record<string, FieldSchema>;
  /** Required fields list */
  required?: string[];
  /** Form-level validators */
  validators?: Validator<Record<string, any>>[];
  /** Form-level async validators */
  asyncValidators?: AsyncValidator<Record<string, any>>[];
}

/**
 * Create a required field validator
 *
 * Purpose: Factory function for required field validation
 * Logic: Checks if value exists and is not empty
 *
 * @param message - Custom error message
 * @returns Validator function for required fields
 */
export const required =
  (message?: string): Validator =>
  (value: any) => {
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0);

    return {
      valid: !isEmpty,
      errors: isEmpty ? [message || 'This field is required'] : undefined,
    };
  };

/**
 * Create a minimum length validator
 *
 * Purpose: Factory function for minimum length validation
 * Logic: Checks if value meets minimum length requirement
 *
 * @param minLength - Minimum required length
 * @param message - Custom error message
 * @returns Validator function for minimum length
 */
export const minLength =
  (minLength: number, message?: string): Validator =>
  (value: any) => {
    if (value === null || value === undefined) {
      return { valid: true };
    }

    const length =
      typeof value === 'string' ? value.length : Array.isArray(value) ? value.length : 0;

    return {
      valid: length >= minLength,
      errors:
        length < minLength ? [message || `Minimum length is ${minLength} characters`] : undefined,
    };
  };

/**
 * Create a maximum length validator
 *
 * Purpose: Factory function for maximum length validation
 * Logic: Checks if value doesn't exceed maximum length
 *
 * @param maxLength - Maximum allowed length
 * @param message - Custom error message
 * @returns Validator function for maximum length
 */
export const maxLength =
  (maxLength: number, message?: string): Validator =>
  (value: any) => {
    if (value === null || value === undefined) {
      return { valid: true };
    }

    const length =
      typeof value === 'string' ? value.length : Array.isArray(value) ? value.length : 0;

    return {
      valid: length <= maxLength,
      errors:
        length > maxLength ? [message || `Maximum length is ${maxLength} characters`] : undefined,
    };
  };

/**
 * Create a pattern validator
 *
 * Purpose: Factory function for regex pattern validation
 * Logic: Tests value against regular expression pattern
 *
 * @param pattern - Regular expression pattern
 * @param message - Custom error message
 * @returns Validator function for pattern matching
 */
export const pattern =
  (pattern: RegExp, message?: string): Validator =>
  (value: any) => {
    if (value === null || value === undefined || value === '') {
      return { valid: true };
    }

    const stringValue = String(value);
    return {
      valid: pattern.test(stringValue),
      errors: !pattern.test(stringValue)
        ? [message || 'Value does not match required pattern']
        : undefined,
    };
  };

/**
 * Create an email validator
 *
 * Purpose: Factory function for email format validation
 * Logic: Uses email regex pattern and built-in email validation
 *
 * @param message - Custom error message
 * @returns Validator function for email addresses
 */
export const email =
  (message?: string): Validator =>
  (value: any) => {
    if (value === null || value === undefined || value === '') {
      return { valid: true };
    }

    return {
      valid: validateEmail(value),
      errors: !validateEmail(value) ? [message || 'Please enter a valid email address'] : undefined,
    };
  };

/**
 * Create a numeric validator
 *
 * Purpose: Factory function for numeric value validation
 * Logic: Checks if value is a valid number within optional range
 *
 * @param min - Minimum value (optional)
 * @param max - Maximum value (optional)
 * @param message - Custom error message
 * @returns Validator function for numeric values
 */
export const numeric =
  (min?: number, max?: number, message?: string): Validator =>
  (value: any) => {
    if (value === null || value === undefined || value === '') {
      return { valid: true };
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      return {
        valid: false,
        errors: [message || 'Value must be a number'],
      };
    }

    if (min !== undefined && numValue < min) {
      return {
        valid: false,
        errors: [message || `Value must be at least ${min}`],
      };
    }

    if (max !== undefined && numValue > max) {
      return {
        valid: false,
        errors: [message || `Value must be at most ${max}`],
      };
    }

    return { valid: true };
  };

/**
 * Create a custom validator with async support
 *
 * Purpose: Factory function for custom validation logic
 * Logic: Wraps custom validation function with error handling
 *
 * @param validatorFn - Custom validator function
 * @param message - Default error message
 * @returns Validator function
 */
export const custom = (
  validatorFn: (value: any) => boolean | Promise<boolean>,
  message?: string
): Validator | AsyncValidator => {
  return async (value: any) => {
    try {
      const result = await validatorFn(value);
      return {
        valid: result,
        errors: !result ? [message || 'Validation failed'] : undefined,
      };
    } catch (error) {
      return {
        valid: false,
        errors: [error instanceof Error ? error.message : 'Validation error'],
      };
    }
  };
};

/**
 * Combine multiple validators
 *
 * Purpose: Executes multiple validators on a single value
 * Logic: Runs all validators and combines results
 *
 * @param validators - Array of validator functions
 * @returns Combined validation result
 */
export const combineValidators =
  (...validators: Validator[]): Validator =>
  (value: any) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    for (const validator of validators) {
      const result = validator(value);
      if (!result.valid) {
        if (result.errors) {
          errors.push(...result.errors);
        }
      }
      if (result.warnings) {
        warnings.push(...result.warnings);
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  };

/**
 * Validate a single field against its schema
 *
 * Purpose: Validates a field value against its validation rules
 * Logic: Applies all validators defined in the field schema
 *
 * @param value - Field value to validate
 * @param fieldSchema - Field validation schema
 * @returns Validation result
 */
export const validateField = (value: any, fieldSchema: FieldSchema): ValidationResult => {
  const validators: Validator[] = [];

  // Add required validator
  if (fieldSchema.required) {
    validators.push(required(fieldSchema.messages?.required));
  }

  // Add type-specific validators
  if (fieldSchema.type) {
    switch (fieldSchema.type) {
      case 'email':
        validators.push(email(fieldSchema.messages?.email));
        break;
      case 'number':
        validators.push(numeric(fieldSchema.min, fieldSchema.max, fieldSchema.messages?.numeric));
        break;
      case 'string':
        if (fieldSchema.min) {
          validators.push(minLength(fieldSchema.min, fieldSchema.messages?.minLength));
        }
        if (fieldSchema.max) {
          validators.push(maxLength(fieldSchema.max, fieldSchema.messages?.maxLength));
        }
        break;
    }
  }

  // Add pattern validator
  if (fieldSchema.pattern) {
    validators.push(pattern(fieldSchema.pattern, fieldSchema.messages?.pattern));
  }

  // Add custom validators
  if (fieldSchema.validators) {
    validators.push(...fieldSchema.validators);
  }

  // Combine and execute validators
  return combineValidators(...validators)(value);
};

/**
 * Validate entire form against schema
 *
 * Purpose: Validates all form fields against their schemas
 * Logic: Validates each field and combines results
 *
 * @param data - Form data to validate
 * @param schema - Form validation schema
 * @returns Validation result with field-specific errors
 */
export const validateFormSchema = (
  data: Record<string, any>,
  schema: FormSchema
): {
  valid: boolean;
  errors: Record<string, string[]>;
  warnings?: Record<string, string[]>;
} => {
  const fieldErrors: Record<string, string[]> = {};
  const fieldWarnings: Record<string, string[]> = {};
  let isValid = true;

  // Validate each field
  for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
    const fieldValue = data[fieldName];
    const result = validateField(fieldValue, fieldSchema);

    if (!result.valid) {
      isValid = false;
      if (result.errors) {
        fieldErrors[fieldName] = result.errors;
      }
    }

    if (result.warnings) {
      fieldWarnings[fieldName] = result.warnings;
    }
  }

  // Execute form-level validators
  if (schema.validators) {
    for (const validator of schema.validators) {
      const result = validator(data);
      if (!result.valid) {
        isValid = false;
        if (result.errors) {
          fieldErrors._form = [...(fieldErrors._form || []), ...result.errors];
        }
      }
      if (result.warnings) {
        fieldWarnings._form = [...(fieldWarnings._form || []), ...result.warnings];
      }
    }
  }

  return {
    valid: isValid,
    errors: fieldErrors,
    warnings: Object.keys(fieldWarnings).length > 0 ? fieldWarnings : undefined,
  };
};

/**
 * Async validation for forms
 *
 * Purpose: Handles asynchronous validation (e.g., server-side checks)
 * Logic: Executes async validators and combines results
 *
 * @param data - Form data to validate
 * @param schema - Form validation schema
 * @returns Promise resolving to validation result
 */
export const validateFormSchemaAsync = async (
  data: Record<string, any>,
  schema: FormSchema
): Promise<{
  valid: boolean;
  errors: Record<string, string[]>;
  warnings?: Record<string, string[]>;
}> => {
  // First run synchronous validation
  const syncResult = validateFormSchema(data, schema);

  const fieldErrors = { ...syncResult.errors };
  const fieldWarnings = { ...syncResult.warnings };
  let isValid = syncResult.valid;

  // Run async validators for each field
  for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
    if (fieldSchema.asyncValidators) {
      const fieldValue = data[fieldName];

      for (const asyncValidator of fieldSchema.asyncValidators) {
        try {
          const result = await asyncValidator(fieldValue);
          if (!result.valid) {
            isValid = false;
            if (result.errors) {
              fieldErrors[fieldName] = [...(fieldErrors[fieldName] || []), ...result.errors];
            }
          }
          if (result.warnings) {
            fieldWarnings[fieldName] = [...(fieldWarnings[fieldName] || []), ...result.warnings];
          }
        } catch (error) {
          isValid = false;
          const errorMessage = error instanceof Error ? error.message : 'Async validation failed';
          fieldErrors[fieldName] = [...(fieldErrors[fieldName] || []), errorMessage];
        }
      }
    }
  }

  // Run form-level async validators
  if (schema.asyncValidators) {
    for (const asyncValidator of schema.asyncValidators) {
      try {
        const result = await asyncValidator(data);
        if (!result.valid) {
          isValid = false;
          if (result.errors) {
            fieldErrors._form = [...(fieldErrors._form || []), ...result.errors];
          }
        }
        if (result.warnings) {
          fieldWarnings._form = [...(fieldWarnings._form || []), ...result.warnings];
        }
      } catch (error) {
        isValid = false;
        const errorMessage = error instanceof Error ? error.message : 'Async validation failed';
        fieldErrors._form = [...(fieldErrors._form || []), errorMessage];
      }
    }
  }

  return {
    valid: isValid,
    errors: fieldErrors,
    warnings: Object.keys(fieldWarnings).length > 0 ? fieldWarnings : undefined,
  };
};
